// src/pages/auth/Login.js
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Typography,
  Box,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import { lightTheme, darkTheme } from '../../themes/themes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { motion } from 'framer-motion';
import { fetchUser } from '../../store/Slices/userSlice';
import './login.css';
import logo from '../../Components/NavComponents/logo.png';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import axios from 'axios';
import BASE_URL from '../../API/config';

// CSS لتأثير الخلفية والنص
const styles = `
  .logo-background::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: 260px auto;
    background-position: top right;
    background-repeat: no-repeat;
    background-image: var(--bg-url);
    opacity: 0.08;
    z-index: 0;
    transform: translate(10%, -10%);
  }

  .logo-background {
    position: relative;
    overflow: hidden;
  }

  .logo-background > * {
    position: relative;
    z-index: 1;
  }

  .text-style {
    position: relative;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    font-weight: 700;
    letter-spacing: 0.6px;
    font-size: 32px;
    color: #F3BA2F;
  }

  .text-style > span.hollow {
    -webkit-text-fill-color: transparent;
    text-fill-color: transparent;
    -webkit-text-stroke: 1.6px #F3BA2F;
    text-stroke: 1.6px #F3BA2F;
    font-size: 36px;
  }

  .text-style > span.solid {
    -webkit-text-fill-color: #ffffff;
    text-fill-color: #ffffff;
  }
`;

const Login = () => {
  const isLightTheme = useSelector((state) => state.theme.light);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: user } = useSelector((state) => state.user);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openForgot, setOpenForgot] = useState(false);

  useEffect(() => {
    if (user && localStorage.getItem('token')) {
      navigate('/home');
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const validateEmail = (email) => {
    const gmailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    return gmailRegex.test(email);
  };

  const handleManualLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill all fields');
      return;
    }

    if (!validateEmail(email)) {
      setError('Email must be a valid Gmail address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/auth/login`, { email, password });
      const { token } = response.data;
      localStorage.setItem('token', token);
      await dispatch(fetchUser()).unwrap();

      setEmail('');
      setPassword('');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={isLightTheme ? lightTheme : darkTheme}>
      <style>{styles}</style>

      <Box className="login-container">
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            className="login-grid"
            alignItems="stretch"
          >
            {/* Left side: branding / hero */}
            <Grid
              item
              xs={12}
              md={6}
              className="login-left"
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Box className="login-hero-card">
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h5"
                      className="text-style"
                    >
                      <span className="hollow">1</span>
                      <span className="solid">Crypto</span>
                      <span className="hollow">X</span>
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ mt: 1.5, color: 'rgba(255,255,255,0.72)' }}
                    >
                      Secure. Fast. Global-grade trading experience.
                    </Typography>
                  </Box>

                  <Stack spacing={2.5} sx={{ mt: 2 }}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'rgba(243,186,47,0.9)', mb: 0.5 }}
                      >
                        Enterprise-grade security
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        Multi-layer security, real-time monitoring, and encrypted sessions
                        for every login.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'rgba(243,186,47,0.9)', mb: 0.5 }}
                      >
                        Advanced trading infrastructure
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        Optimized for high-frequency traders and long-term investors alike.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'rgba(243,186,47,0.9)', mb: 0.5 }}
                      >
                        AI-powered signals (Coming soon)
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        Stay ahead with intelligent alerts, signals, and risk analytics.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ mt: 4 }}>
                    <Chip
                      label="Welcome back to 1CryptoX"
                      className="login-chip"
                    />
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            {/* Right side: login form */}
            <Grid item xs={12} md={6} className="login-right">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Card
                  className="logo-background login-card"
                  style={{ '--bg-url': `url(${logo})` }}
                  sx={{
                    borderRadius: 3,
                    backdropFilter: 'blur(18px)',
                    background:
                      'linear-gradient(135deg, rgba(15,23,42,0.97), rgba(17,24,39,0.96))',
                    border: '1px solid rgba(148,163,184,0.18)',
                    boxShadow:
                      '0 18px 45px rgba(0,0,0,0.55), 0 0 0 1px rgba(148,163,184,0.25)',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <Typography
                        variant="h5"
                        sx={{
                          color: '#F3BA2F',
                          fontWeight: 700,
                          letterSpacing: 0.6,
                          mb: 0.5,
                        }}
                      >
                        Sign in to your account
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(226,232,240,0.75)' }}
                      >
                        Use your registered Gmail address to access 1CryptoX.
                      </Typography>
                    </Box>

                    <form onSubmit={handleManualLogin}>
                      <TextField
                        label="Email (Gmail only)"
                        type="email"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2.5 }}
                        variant="outlined"
                        autoComplete="email"
                        InputLabelProps={{ shrink: true }}
                        FormHelperTextProps={{ sx: { mt: 0.3 } }}
                        helperText="Please use the Gmail you registered with."
                      />

                      <TextField
                        label="Password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 1.5 }}
                        variant="outlined"
                        autoComplete="current-password"
                        InputLabelProps={{ shrink: true }}
                      />

                      {/* Forgot Password */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mb: 2,
                        }}
                      >
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setOpenForgot(true)}
                          sx={{
                            textTransform: 'none',
                            color: '#93C5FD',
                            fontSize: 13,
                            '&:hover': {
                              color: '#BFDBFE',
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Forgot Password?
                        </Button>
                      </Box>

                      {error && (
                        <Typography
                          color="error"
                          sx={{
                            mb: 2,
                            textAlign: 'center',
                            fontSize: 13,
                          }}
                        >
                          {error}
                        </Typography>
                      )}

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={isLoading}
                        sx={{
                          bgcolor: '#F0B90B',
                          color: '#111827',
                          fontWeight: 600,
                          borderRadius: 2,
                          py: 1.4,
                          fontSize: 15,
                          boxShadow:
                            '0 8px 22px rgba(240,185,11,0.4), 0 0 0 1px rgba(250,204,21,0.4)',
                          '&:hover': {
                            bgcolor: '#DAA520',
                            boxShadow:
                              '0 10px 26px rgba(240,185,11,0.55), 0 0 0 1px rgba(250,204,21,0.55)',
                          },
                        }}
                      >
                        {isLoading ? 'Logging in...' : 'Login'}
                      </Button>
                    </form>

                    <Divider
                      sx={{
                        my: 3,
                        bgcolor: 'rgba(148,163,184,0.35)',
                        '&::before, &::after': {
                          borderColor: 'rgba(148,163,184,0.35)',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(148,163,184,0.9)' }}
                      >
                        Or continue with
                      </Typography>
                    </Divider>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Button
                        onClick={() =>
                          alert('Login with Google clicked! (OAuth not implemented yet)')
                        }
                        variant="outlined"
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          py: 1.2,
                          borderColor: 'rgba(156,163,175,0.6)',
                          color: '#E5E7EB',
                          textTransform: 'none',
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          '&:hover': {
                            borderColor: '#F3BA2F',
                            backgroundColor: 'rgba(24, 37, 55, 0.9)',
                          },
                        }}
                      >
                        <FontAwesomeIcon icon={faGoogle} size="lg" />
                        <span>Google</span>
                      </Button>

                      <Button
                        onClick={() =>
                          alert('Login with X clicked! (OAuth not implemented yet)')
                        }
                        variant="outlined"
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          py: 1.2,
                          borderColor: 'rgba(156,163,175,0.6)',
                          color: '#E5E7EB',
                          textTransform: 'none',
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          '&:hover': {
                            borderColor: '#F3BA2F',
                            backgroundColor: 'rgba(15,23,42,0.95)',
                          },
                        }}
                      >
                          <FontAwesomeIcon icon={faXTwitter} size="lg" />
                          <span>Continue with X</span>
                      </Button>
                    </Box>

                    <Typography
                      sx={{
                        mt: 1,
                        textAlign: 'center',
                        color: 'rgba(148,163,184,0.95)',
                        fontSize: 14,
                      }}
                    >
                      Don’t have an account?{' '}
                      <Link
                        href="/register"
                        sx={{
                          color: '#F3BA2F',
                          fontWeight: 500,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Register
                      </Link>
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* نافذة تغيير كلمة المرور */}
      <ForgotPasswordDialog
        open={openForgot}
        onClose={() => setOpenForgot(false)}
        defaultEmail={email}
      />
    </ThemeProvider>
  );
};

export default Login;
